"use client";

import {
  Close,
  MicOff,
  MicRounded,
  PauseCircle,
  PauseCircleFilled,
  Repeat,
  RepeatOn,
} from "@mui/icons-material";
import { useState, useRef, useEffect, forwardRef } from "react";
import GridLayout from "react-grid-layout";
import "react-grid-layout/css/styles.css";

import "./SelectedArrayContainer.css";
import { Box, Button, ButtonGroup } from "@mui/material";
import { ChevronLeft, ChevronRight, PlayCircle } from "@mui/icons-material";

import { scrollToCenter } from "../../../../utils/utility";
import { uvenuFetcher } from "../../../../utils/uvenu-fetcher";
import { URLS } from "../../../../constants";
const SelectedArray = forwardRef((props, ref) => {
  const {
    eventId,
    loggedInUser,
    selections,
    socketEmit,
    isSocketConnected,
    index,
    activeTabIndex,
  } = props;
  // Initial media items
  const [mediaItems, setMediaItems] = useState(selections.items);
  const [layout, setLayout] = useState(selections.layouts || []);
  const [activeItem, setActiveItem] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [isVideoLooping, setIsVideoLooping] = useState(true);
  const [isVideoMuted, setIsVideoMuted] = useState(true);

  const activeItemId = useRef(null);

  const itemsAndLayoutRef = useRef({ items: mediaItems, layouts: layout });

  useEffect(() => {
    setMediaItems(selections.items);
    setLayout(selections.layouts);
    itemsAndLayoutRef.current = { ...selections };
  }, [selections]);

  const saveSelectedItems = async () => {
    try {
      if (
        !itemsAndLayoutRef.current.items &&
        !itemsAndLayoutRef.current.layouts
      ) {
        return;
      }
      const payload = {
        items: itemsAndLayoutRef.current.items,
        layouts: itemsAndLayoutRef.current.layouts,
        eventId,
        _id: itemsAndLayoutRef.current._id,
      };
      const response = await uvenuFetcher({
        method: "PATCH",
        url: URLS.SAVE_SELECTED_CONTROLLABLE_ITEMS,
        headers: { token: loggedInUser.token },
        body: payload,
      });
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    if (mediaItems.length) {
      if (activeItemId.current) {
        if (!mediaItems.some((i) => i.id === activeItemId.current)) {
          activeItemId.current = null;
          setActiveItem(0);
          return;
        }
        const newActiveIndex = mediaItems.findIndex(
          (item) => item.id === activeItemId.current
        );
        setActiveItem(newActiveIndex);
      }
    } else {
      setActiveItem(0);
      activeItemId.current = null;
    }
  }, [layout, mediaItems]);

  useEffect(() => {
    document.querySelectorAll(`video[id^="co-video"]`).forEach((video) => {
      video.pause();
      video.currentTime = 0; // Reset to the beginning (optional)
    });

    setIsVideoPlaying(false);
    setIsVideoMuted(true);
    setIsVideoLooping(false);

    if (index === activeTabIndex) {
      socketEmit({ activeItem: mediaItems[activeItem] });
    }

    if (mediaItems[activeItem]) {
      scrollToCenter(".co-drop-zone", `#meida-${mediaItems[activeItem].id}`);
    }
  }, [activeItem]);

  const dropZoneRef = useRef(null);
  const nextId = useRef(mediaItems.length + 1);

  // Handle layout change (reordering)
  const onLayoutChange = (layout, allLayouts) => {
    const newArray = layout.sort((a, b) => a["x"] - b["x"]);

    newArray.forEach((l) => {});
    const newItems = layout.map((l) => {
      return mediaItems.find((m) => l.i === m.id);
    });
    itemsAndLayoutRef.current.items = newItems;
    itemsAndLayoutRef.current.layouts = layout;
    setMediaItems(newItems);
    setLayout(layout);
    saveSelectedItems();
  };

  // Remove an item
  const removeItem = (id) => {
    const newItems = mediaItems.filter((item) => item.id !== id);
    const newLayout = layout.filter((item) => item.i !== id);
    setMediaItems(newItems);
    setLayout(newLayout);
    itemsAndLayoutRef.current.items = newItems;
    itemsAndLayoutRef.current.layouts = newLayout;
  };

  // Handle drop from outside
  const handleDrop = (e) => {
    const data = e.dataTransfer.getData("text/plain");
    const dataObject = JSON.parse(data);
    e.preventDefault();
    if (mediaItems.some((item) => item.id === dataObject.id)) {
      alert("Item already exists in the list");
      return;
    }
    // In a real app, you would handle the dropped file/media
    // For this example, we'll just add a new placeholder
    const newId = nextId.current.toString();
    nextId.current += 1;
    const newItem = {
      id: dataObject?.id || newId,
      content: dataObject?.mediaFileUrl,
      mimeType: dataObject?.mimeType,
      comment: dataObject?.comment,
    };
    setMediaItems([...mediaItems, newItem]);
    itemsAndLayoutRef.current.items = [...mediaItems, newItem];

    const newLayout = {
      i: newItem.id,
      x: layout.length * 1,
      y: 0,
      w: 1,
      h: 1,
    };

    setLayout([...layout, newLayout]);
    itemsAndLayoutRef.current.layouts = [...layout, newLayout];
  };

  // Setup drag-and-drop event handlers
  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleVideoPlayPause = (item) => {
    // Play video
    if (isVideoPlaying) {
      setIsVideoPlaying(false);
      socketEmit({ play: false, id: item.id });
    } else {
      setIsVideoPlaying(true);
      socketEmit({ play: true, id: item.id });
    }
  };
  const handleMute = (item) => {
    // Play video
    if (isVideoMuted) {
      setIsVideoMuted(false);
      socketEmit({ mute: false, id: item.id });
    } else {
      setIsVideoMuted(true);
      socketEmit({ mute: true, id: item.id });
    }
  };

  const handleLoop = (item) => {
    // Play video
    if (isVideoLooping) {
      setIsVideoLooping(false);
      socketEmit({ loop: false, id: item.id });
    } else {
      setIsVideoLooping(true);
      socketEmit({ loop: true, id: item.id });
    }
  };
  const handlePreviewVideoPlay = (e) => {
    const targetElement = e.target;
    document.querySelectorAll(`video[id^="co-video"]`).forEach((video) => {
      const mediaCard = video.closest(".media-item");
      const playButton = mediaCard.querySelector(".co-preview-video-play");
      const pauseButton = mediaCard.querySelector(".co-preview-video-pause");
      playButton.style.display = "block";
      pauseButton.style.display = "none";
      video.pause();
    });
    const mediaCard = targetElement.closest(".media-item");
    const videoElement = mediaCard.querySelector("video");
    const playButton = mediaCard.querySelector(".co-preview-video-play");
    const pauseButton = mediaCard.querySelector(".co-preview-video-pause");

    if (videoElement) {
      videoElement.play();
      playButton.style.display = "none";
      pauseButton.style.display = "block";
    }
  };
  const handlePreviewVideoPause = (e) => {
    const targetElement = e.target;

    const mediaCard = targetElement.closest(".media-item");
    const videoElement = mediaCard.querySelector("video");
    const playButton = mediaCard.querySelector(".co-preview-video-play");
    const pauseButton = mediaCard.querySelector(".co-preview-video-pause");

    if (videoElement) {
      videoElement.pause();
      playButton.style.display = "block";
      pauseButton.style.display = "none";
    }
  };

  return (
    <>
      {" "}
      <div className="media-column-container" ref={ref}>
        <div
          className="co-drop-zone"
          ref={dropZoneRef}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
        >
          <GridLayout
            className="layout"
            layout={layout}
            cols={Math.max(12, mediaItems.length * 4)}
            rowHeight={200}
            width={Math.max(1518, mediaItems.length * 506)}
            onLayoutChange={onLayoutChange}
            margin={[8, 8]}
            autoSize={true}
            isDraggable={true}
            isResizable={false}
            compactType="horizontal"
            draggableHandle=".drag-handle"
          >
            {mediaItems.map((item, index) => (
              <div
                id={`meida-${item.id}`}
                key={item.id}
                className={`media-item-wrapper ${
                  index === activeItem ? "highlight" : ""
                }`}
              >
                <div className="media-item" key={item.id}>
                  <div className="drag-handle" />
                  <button
                    className="remove-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeItem(item.id);
                    }}
                    aria-label="Remove item"
                  >
                    <Close size={16} />
                  </button>
                  {
                    // If the media item is a video, render the video element
                    item?.mimeType?.includes("video") ? (
                      <>
                        <video
                          src={item.content}
                          className="media-content"
                          id={`co-video-${item.id}`}
                          loop
                        ></video>
                        <PlayCircle
                          className="co-preview-video-play"
                          onClick={handlePreviewVideoPlay}
                          id={`co-video-${item.id}-play`}
                          style={{
                            display: "block",
                          }}
                        />
                        <PauseCircleFilled
                          onClick={handlePreviewVideoPause}
                          className="co-preview-video-pause"
                          id={`co-video-${item.id}-pause`}
                          style={{
                            display: "none",
                          }}
                        />
                      </>
                    ) : item?.mimeType?.includes("image") ? (
                      <img
                        src={item.content || "/placeholder.svg"}
                        alt={`Media item ${item.id}`}
                        className="media-content"
                      />
                    ) : item.comment ? (
                      <div
                        className="soImageMedia quoteOnlyCard"
                        style={{ backgroundColor: "rgb(13, 14, 19)" }}
                      >
                        {item.comment}
                      </div>
                    ) : null
                  }
                </div>
              </div>
            ))}
          </GridLayout>
        </div>
        <div className="co-controls-container">
          <Box sx={{ p: 1 }}>
            {isSocketConnected ? "🟢 Connected to Output" : "🔴 Connecting.."}
          </Box>
          <div className="co-controls">
            <ButtonGroup
              variant="contained"
              fullWidth
              size="large"
              disabled={!isSocketConnected}
            >
              <Button
                sx={{ color: "white" }}
                onClick={() => {
                  const previousItemIndex =
                    activeItem === 0 ? mediaItems.length - 1 : activeItem - 1;
                  setActiveItem(previousItemIndex);
                  activeItemId.current =
                    mediaItems[previousItemIndex]?.id || null;
                }}
              >
                <ChevronLeft />
              </Button>
              <Button
                sx={{ color: "white" }}
                onClick={() => {
                  const nextItemIndex = (activeItem + 1) % mediaItems.length;
                  setActiveItem(nextItemIndex);
                  activeItemId.current = mediaItems[nextItemIndex]?.id || null;
                }}
              >
                <ChevronRight />
              </Button>
            </ButtonGroup>
          </div>

          <ButtonGroup
            variant="contained"
            disabled={
              !mediaItems[activeItem]?.mimeType?.includes("video") ||
              !isSocketConnected
            }
            orientation="vertical"
            fullWidth
          >
            <Button
              className="co-play-button"
              onClick={() => {
                handleVideoPlayPause(mediaItems[activeItem]);
              }}
            >
              {isVideoPlaying ? <PauseCircle /> : <PlayCircle />}
            </Button>
            <Button
              className="co-play-button"
              onClick={() => {
                handleMute(mediaItems[activeItem]);
              }}
            >
              {isVideoMuted ? <MicOff /> : <MicRounded />}
            </Button>
            <Button
              className="co-play-button"
              onClick={() => handleLoop(mediaItems[activeItem])}
            >
              {isVideoLooping ? <RepeatOn /> : <Repeat />}
            </Button>
          </ButtonGroup>
        </div>
      </div>
    </>
  );
});
export default SelectedArray;
